import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import ZenPagination from "../components/ZenPagination";
import {
  Package, Plus, Trash2, Pencil, Search, Camera, Upload, FileDown,
  CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp, Eye,
  RefreshCw, Loader2, Building2, Calendar, Hash, X, Check, Printer, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { UICtx, AuthCtx } from "../appContexts";
import { ZEN_PDF_FONT_FACE } from "../quotes";
import { ModuleIcon } from "../components/layout/Sidebar";
import { QuoteViewsModal } from "./QuoteBuilderPage";

// ── PDF Generation ──────────────────────────────────────────────────────────
async function generatePoPDF(po) {
  // Firma bilgisini settings'ten al
  let companyName = "ZEN ERP";
  let companyPhone = "";
  let companyAddress = "";
  try {
    const { data: settings } = await api.get("/settings");
    const get = (key) => (settings || []).find(s => s.key === key)?.value || "";
    companyName  = get("company_name")  || "ZEN Gölgelendirme Sistemleri";
    companyPhone = get("company_phone") || "";
    companyAddress = get("company_address") || "";
  } catch (e) { /* varsayılanları kullan */ }

  const STATUS_LABELS = {
    taslak: "Taslak", onaylandi: "Onaylandı", teslim_alindi: "Teslim Alındı", iptal: "İptal",
  };
  const stLabel = STATUS_LABELS[po.status] || po.status;
  const items = po.items || [];
  const total = po.total_amount || 0;
  const currency = po.total_currency || "TRY";

  const rowsHtml = items.map((it, i) => `
    <tr style="background:${i % 2 === 0 ? "#fff" : "#f8f9fb"}">
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#374151">${i + 1}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#374151;font-weight:600">${it.code || "-"}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#374151">${it.label || "-"}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#374151;text-align:center">${it.dimension || "-"}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#374151;text-align:center">${it.qty || 1} ${it.unit || "ad"}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#374151;text-align:right">${Number(it.unit_price || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${it.currency || currency}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;font-weight:700;color:#1e293b;text-align:right">${(Number(it.unit_price || 0) * Number(it.qty || 1)).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${it.currency || currency}</td>
    </tr>
  `).join("");

  const html = `<!DOCTYPE html>
<html lang="tr">
<head>
<meta charset="UTF-8"/>
<style>
${ZEN_PDF_FONT_FACE}
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: ZenSans, 'Helvetica Neue', Arial, sans-serif; font-size: 12px; color: #1e293b; background: #fff; padding: 32px; }
  .header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 28px; padding-bottom: 20px; border-bottom: 2.5px solid #e5322a; }
  .logo-area img { height: 40px; object-fit: contain; margin-bottom: 4px; }
  .logo-area h1 { font-size: 22px; font-weight: 900; color: #e5322a; letter-spacing: -0.5px; }
  .logo-area p { font-size: 10px; color: #64748b; margin-top: 2px; }
  .doc-title { text-align: right; }
  .doc-title h2 { font-size: 18px; font-weight: 800; color: #1e293b; }
  .doc-title .doc-no { font-size: 13px; font-weight: 700; color: #e5322a; font-family: ZenMono, monospace; margin-top: 4px; }
  .doc-title .doc-status { display: inline-block; margin-top: 6px; padding: 2px 10px; border-radius: 20px; font-size: 10px; font-weight: 700; background: #dbeafe; color: #1d4ed8; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 22px; }
  .info-box { background: #f8f9fb; border-radius: 8px; padding: 12px 14px; }
  .info-box label { font-size: 9px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.8px; }
  .info-box p { font-size: 13px; font-weight: 700; color: #1e293b; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  thead tr { background: #1e293b; }
  thead th { padding: 9px 10px; font-size: 10px; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 0.5px; text-align: left; }
  thead th:nth-child(5), thead th:nth-child(6), thead th:nth-child(7) { text-align: right; }
  .total-row { display: flex; justify-content: flex-end; padding: 10px 0; }
  .total-box { background: #1e293b; color: #fff; border-radius: 8px; padding: 12px 20px; text-align: right; min-width: 200px; }
  .total-box .label { font-size: 10px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
  .total-box .amount { font-size: 20px; font-weight: 900; margin-top: 2px; }
  .notes-box { background: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px; padding: 12px 14px; margin-top: 16px; }
  .notes-box label { font-size: 9px; font-weight: 700; color: #ea580c; text-transform: uppercase; letter-spacing: 0.8px; }
  .notes-box p { font-size: 11px; color: #7c3a00; margin-top: 4px; white-space: pre-wrap; }
  .footer { margin-top: 28px; padding-top: 14px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
  .footer p { font-size: 9px; color: #94a3b8; }
</style>
</head>
<body>
<div class="header">
  <div class="logo-area">
    <img src="zen-logo.png" alt="${companyName}" />
    <p>${[companyPhone, companyAddress].filter(Boolean).join(" · ")}</p>
  </div>
  <div class="doc-title">
    <h2>ALIŞ SİPARİŞİ</h2>
    <div class="doc-no">${po.id}</div>
    <div class="doc-status">${stLabel}</div>
  </div>
</div>

<div class="info-grid">
  <div class="info-box">
    <label>Tedarikçi</label>
    <p>${po.supplier_name || "—"}</p>
  </div>
  <div class="info-box">
    <label>Sipariş Tarihi</label>
    <p>${po.order_date || "—"}</p>
  </div>
</div>

<table>
  <thead>
    <tr>
      <th style="width:32px">#</th>
      <th>Ürün Kodu</th>
      <th>Açıklama</th>
      <th style="text-align:center">Ölçü / Boyut</th>
      <th style="text-align:right">Miktar</th>
      <th style="text-align:right">Birim Fiyat</th>
      <th style="text-align:right">Toplam</th>
    </tr>
  </thead>
  <tbody>${rowsHtml}</tbody>
</table>

<div class="total-row">
  <div class="total-box">
    <div class="label">Genel Toplam</div>
    <div class="amount">${total.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}</div>
  </div>
</div>

${po.notes ? `<div class="notes-box"><label>Notlar</label><p>${po.notes}</p></div>` : ""}

<div class="footer">
  <p>${companyName} — Alış Siparişi Belgesi</p>
  <p>Oluşturulma: ${new Date().toLocaleString("tr-TR")}</p>
</div>
</body>
</html>`;

  const { data } = await api.post("/share/html-pdf", {
    html,
    filename: `AlısSiparisi-${po.id}.pdf`,
    refType: "purchase_order",
    refId: po.id,
  });
  return `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
}

const STATUS_MAP = {
  taslak:         { label: "Taslak",         color: "bg-slate-400" },
  onaylandi:      { label: "Onaylandı",       color: "bg-blue-500" },
  teslim_alindi:  { label: "Teslim Alındı",   color: "bg-emerald-500" },
  iptal:          { label: "İptal",           color: "bg-red-500" },
};

const EMPTY_PO = {
  supplier_name: "", order_date: new Date().toISOString().slice(0, 10),
  status: "taslak", items: [], total_amount: 0, total_currency: "TRY", notes: "",
};

function compressImage(file, maxPx = 1400) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          const reader2 = new FileReader();
          reader2.onload = (e2) => resolve({
            base64: e2.result.split(",")[1],
            type: "jpeg",
            previewUrl: e2.result,
          });
          reader2.readAsDataURL(blob);
        }, "image/jpeg", 0.82);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ── Comparison badge ─────────────────────────────────────────────────────
function DiffBadge({ item }) {
  if (!item.matched) return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-[#22304A] text-slate-500">
      <AlertTriangle className="w-3 h-3" /> Eşleşme yok
    </span>
  );
  const d = item.diff_pct;
  if (d === null || d === undefined) return null;
  const ok = Math.abs(d) <= 5;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold
      ${ok ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
           : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"}`}>
      {ok ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {d > 0 ? `+${d}%` : `${d}%`}
      {" "}{item.our_currency} {(item.our_price || 0).toLocaleString("tr-TR")}
    </span>
  );
}

// ── Föy Tarama Paneli ─────────────────────────────────────────────────────
function FoyerScanPanel({ onConfirm }) {
  const [preview, setPreview]         = useState(null);
  const [scanning, setScanning]       = useState(false);
  const [result, setResult]           = useState(null);
  const [edited, setEdited]           = useState(null);   // düzenlenebilir kopya
  const [error, setError]             = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setResult(null); setEdited(null); setError(null);
    const { base64, type, previewUrl } = await compressImage(file);
    setPreview(previewUrl);
    setScanning(true);
    try {
      const { data } = await api.post("/purchase-orders/scan-foyer", { image_base64: base64, image_type: type });
      setResult(data);
      setEdited(JSON.parse(JSON.stringify(data)));  // derin kopya — düzenleme için
      toast.success("Föy tarandı");
    } catch (e) {
      setError(e?.response?.data?.detail || "Tarama başarısız");
      toast.error("Tarama hatası");
    } finally {
      setScanning(false);
    }
  };

  const setField = (k, v) => setEdited(p => ({ ...p, [k]: v }));
  const setItemField = (i, k, v) => setEdited(p => ({
    ...p, items: p.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it),
  }));
  const addItem = () => setEdited(p => ({
    ...p, items: [...(p.items || []), { code: "", label: "", dimension: "", qty: 1, unit: "ad", unit_price: 0, currency: "TRY", status: "new" }],
  }));
  const removeItem = (i) => setEdited(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-4">
      {/* Upload zone */}
      <div
        className="border-2 border-dashed border-slate-300 dark:border-[#22304A] rounded-2xl p-8 text-center
                   hover:border-[#E5322A]/50 transition-colors cursor-pointer"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
      >
        <input ref={fileRef} type="file" accept="image/*" capture="environment"
               className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
        {preview ? (
          <img src={preview} alt="föy" className="max-h-48 mx-auto rounded-xl object-contain" />
        ) : (
          <>
            <div className="flex justify-center gap-4 mb-3">
              <Camera className="w-10 h-10 text-slate-300" />
              <Upload className="w-10 h-10 text-slate-300" />
            </div>
            <div className="text-sm font-semibold text-slate-500">Fotoğraf çek veya dosya yükle</div>
            <div className="text-xs text-slate-400 mt-1">Fatura, irsaliye veya fiyat föyü</div>
          </>
        )}
      </div>

      {scanning && (
        <div className="flex items-center justify-center gap-2 text-sm text-[#E5322A] py-4" data-testid="foyer-scanning">
          <Loader2 className="w-5 h-5 animate-spin" /> AI analiz ediyor...
        </div>
      )}

      {error && (
        <div className="zen-panel p-4 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 text-sm">{error}</div>
      )}

      {edited && (
        <div className="space-y-3" data-testid="foyer-scan-result">
          {/* Üst bilgi — düzenlenebilir */}
          <div className="zen-panel p-4">
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-2">
              <Pencil className="w-3.5 h-3.5" /> AI Sonuçlarını Gözden Geçir ve Düzenle
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase text-slate-400 mb-1">Tedarikçi</label>
                <input
                  className="zen-input text-sm"
                  data-testid="scan-edit-supplier"
                  value={edited.supplier_name || ""}
                  onChange={e => setField("supplier_name", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase text-slate-400 mb-1">Belge No</label>
                <input
                  className="zen-input text-sm"
                  data-testid="scan-edit-invoice-no"
                  value={edited.invoice_no || ""}
                  onChange={e => setField("invoice_no", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase text-slate-400 mb-1">Tarih</label>
                <input
                  type="date"
                  className="zen-input text-sm"
                  data-testid="scan-edit-invoice-date"
                  value={edited.invoice_date || ""}
                  onChange={e => setField("invoice_date", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Kalemler — satır satır düzenlenebilir */}
          <div className="zen-panel overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-[#0C1220]">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Kalemler ({(edited.items || []).length})</span>
              <button onClick={addItem} className="zen-btn-ghost !text-[10px] !py-1 !px-2 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Kalem Ekle
              </button>
            </div>
            <table className="zen-table zen-table--compact text-xs w-full">
              <thead>
                <tr>
                  <th>Kod</th>
                  <th>Açıklama</th>
                  <th className="w-20">Ölçü</th>
                  <th className="w-16">Adet</th>
                  <th className="w-16">Birim</th>
                  <th className="w-24">Birim Fiyat</th>
                  <th className="w-16">Döviz</th>
                  <th className="w-24 text-right">Karşılaştırma</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(edited.items || []).map((it, i) => {
                  const orig = (result?.items || [])[i];
                  return (
                    <tr key={i} className={it.status === "over" ? "bg-red-50 dark:bg-red-900/10" : it.status === "ok" ? "bg-emerald-50 dark:bg-emerald-900/10" : ""}>
                      <td>
                        <input className="zen-input !text-[11px] !py-0.5 w-full min-w-[60px]"
                          data-testid={`scan-item-code-${i}`}
                          value={it.code || ""}
                          onChange={e => setItemField(i, "code", e.target.value)} />
                      </td>
                      <td>
                        <input className="zen-input !text-[11px] !py-0.5 w-full min-w-[100px]"
                          data-testid={`scan-item-label-${i}`}
                          value={it.label || ""}
                          onChange={e => setItemField(i, "label", e.target.value)} />
                      </td>
                      <td>
                        <input className="zen-input !text-[11px] !py-0.5 w-full"
                          value={it.dimension || ""}
                          onChange={e => setItemField(i, "dimension", e.target.value)} />
                      </td>
                      <td>
                        <input type="number" min="0" step="1" className="zen-input !text-[11px] !py-0.5 w-full text-right"
                          data-testid={`scan-item-qty-${i}`}
                          value={it.qty || ""}
                          onChange={e => setItemField(i, "qty", e.target.value)} />
                      </td>
                      <td>
                        <input className="zen-input !text-[11px] !py-0.5 w-full"
                          value={it.unit || ""}
                          onChange={e => setItemField(i, "unit", e.target.value)} />
                      </td>
                      <td>
                        <input type="number" min="0" step="0.01" className="zen-input !text-[11px] !py-0.5 w-full text-right"
                          data-testid={`scan-item-price-${i}`}
                          value={it.unit_price || ""}
                          onChange={e => setItemField(i, "unit_price", e.target.value)} />
                      </td>
                      <td>
                        <select className="zen-input !text-[11px] !py-0.5 w-full"
                          value={it.currency || "TRY"}
                          onChange={e => setItemField(i, "currency", e.target.value)}>
                          <option>TRY</option><option>EUR</option><option>USD</option>
                        </select>
                      </td>
                      <td className="text-right"><DiffBadge item={orig || it} /></td>
                      <td>
                        <button onClick={() => removeItem(i)} className="p-1 text-red-400 hover:text-red-600 transition-colors" title="Sil">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Toplam */}
          <div className="flex items-center justify-between text-sm px-1">
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-semibold uppercase text-slate-400">Genel Toplam</label>
              <input type="number" step="0.01" className="zen-input !text-sm !py-1 w-36 text-right"
                data-testid="scan-edit-total"
                value={edited.grand_total || ""}
                onChange={e => setField("grand_total", parseFloat(e.target.value) || 0)} />
              <select className="zen-input !text-sm !py-1 w-20"
                value={edited.currency || "TRY"}
                onChange={e => setField("currency", e.target.value)}>
                <option>TRY</option><option>EUR</option><option>USD</option>
              </select>
            </div>
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              {result && Object.keys(edited).some(k => JSON.stringify(edited[k]) !== JSON.stringify(result[k])) && (
                <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="w-3 h-3" /> Değişiklikler kaydedilmedi
                </span>
              )}
            </div>
          </div>

          <button
            data-testid="foyer-create-po-btn"
            onClick={() => onConfirm(edited)}
            className="zen-btn-primary w-full"
          >
            <Check className="w-4 h-4" /> Bu Verilerden Sipariş Oluştur
          </button>
        </div>
      )}
    </div>
  );
}

// ── Sipariş Formu ─────────────────────────────────────────────────────────
function PoForm({ initial, suppliers, onSave, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_PO);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addItem = () => set("items", [...form.items, { code: "", label: "", dimension: "", qty: 1, unit: "ad", unit_price: 0, currency: "TRY" }]);
  const setItem = (i, k, v) => set("items", form.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const delItem = (i) => set("items", form.items.filter((_, idx) => idx !== i));

  const total = form.items.reduce((s, it) => s + (parseFloat(it.qty || 0) * parseFloat(it.unit_price || 0)), 0);

  const submit = () => {
    if (!form.supplier_name) { toast.error("Tedarikçi adı gerekli"); return; }
    onSave({ ...form, total_amount: total });
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="zen-label">Tedarikçi Adı *</label>
          <input data-testid="po-supplier-name" value={form.supplier_name}
            onChange={e => set("supplier_name", e.target.value)}
            list="supplier-list" className="zen-input" placeholder="Tedarikçi seç veya yaz..." />
          <datalist id="supplier-list">
            {suppliers.map(s => <option key={s.id} value={s.name} />)}
          </datalist>
        </div>
        <div>
          <label className="zen-label">Sipariş Tarihi</label>
          <input data-testid="po-date" type="date" value={form.order_date}
            onChange={e => set("order_date", e.target.value)} className="zen-input" />
        </div>
        <div>
          <label className="zen-label">Durum</label>
          <select data-testid="po-status" value={form.status}
            onChange={e => set("status", e.target.value)} className="zen-input">
            {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label className="zen-label">Para Birimi</label>
          <select value={form.total_currency} onChange={e => set("total_currency", e.target.value)} className="zen-input">
            {["TRY", "EUR", "USD"].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Kalemler */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="zen-label mb-0">Kalemler</label>
          <button onClick={addItem} className="zen-btn-ghost text-xs !py-1">
            <Plus className="w-3.5 h-3.5" /> Kalem Ekle
          </button>
        </div>
        {form.items.length === 0 && (
          <div className="text-xs text-slate-400 py-3 text-center border border-dashed rounded-xl dark:border-[#22304A]">
            Kalem eklenmedi
          </div>
        )}
        {form.items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-1.5 mb-1.5 items-center">
            <input value={it.code} onChange={e => setItem(i, "code", e.target.value)}
              placeholder="Kod" className="zen-input col-span-2 text-xs" />
            <input value={it.label} onChange={e => setItem(i, "label", e.target.value)}
              placeholder="Açıklama" className="zen-input col-span-3 text-xs" />
            <input value={it.dimension || ""} onChange={e => setItem(i, "dimension", e.target.value)}
              placeholder="Ölçü" className="zen-input col-span-2 text-xs" />
            <input value={it.qty} type="number" onChange={e => setItem(i, "qty", e.target.value)}
              placeholder="Adet" className="zen-input col-span-1 text-xs" />
            <input value={it.unit_price} type="number" onChange={e => setItem(i, "unit_price", e.target.value)}
              placeholder="Fiyat" className="zen-input col-span-2 text-xs" />
            <select value={it.currency || "TRY"} onChange={e => setItem(i, "currency", e.target.value)}
              className="zen-input col-span-1 text-xs">
              {["TRY","EUR","USD"].map(c => <option key={c}>{c}</option>)}
            </select>
            <button onClick={() => delItem(i)} className="zen-btn-ghost !p-1.5 text-red-400 col-span-1">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {form.items.length > 0 && (
          <div className="text-right text-sm font-bold mt-2 text-slate-700 dark:text-slate-200">
            Toplam: {total.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} {form.total_currency}
          </div>
        )}
      </div>

      <div>
        <label className="zen-label">Notlar</label>
        <textarea value={form.notes} onChange={e => set("notes", e.target.value)}
          className="zen-input h-16 resize-none" placeholder="İsteğe bağlı notlar..." />
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="zen-btn-ghost">İptal</button>
        <button data-testid="po-save-btn" onClick={submit} className="zen-btn-primary">
          <Check className="w-4 h-4" /> Kaydet
        </button>
      </div>
    </div>
  );
}

// Tek satırlık "Son Görüntüleme" etiketi
function lastViewLabel(v) {
  if (!v?.lastViewedAt) return null;
  const d = new Date(v.lastViewedAt);
  const now = new Date();
  const dateStr = d.toDateString() === now.toDateString()
    ? "Bugün" : d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" });
  const timeStr = d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  const addrParts = (v.gps?.address || "").split(",").map(s => s.trim()).filter(Boolean);
  const city = addrParts.length > 1 ? addrParts[addrParts.length - 2] : addrParts[0];
  return `Son Görüntüleme: ${dateStr} ${timeStr}${city ? " - " + city : ""}`;
}

// ── Ana Modül ─────────────────────────────────────────────────────────────
export default function PurchaseOrdersModule() {
  const { user } = useContext(AuthCtx);
  const isAdmin = user?.role === "admin";
  const [pos, setPos]       = useState([]);
  const [loadingPo, setLoadingPo] = useState(true);
  const [q, setQ]           = useState("");
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [view, setView]     = useState("list"); // list | new | scan
  const [editing, setEditing] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(null); // po.id
  const [views, setViews]   = useState({});
  const [seenPo, setSeenPo] = useState(null);
  const prevViewsRef = useRef({});

  const load = useCallback(async () => {
    setLoadingPo(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/purchase-orders"),
        api.get("/customers"),
      ]);
      setPos(p.data);
      setSuppliers(c.data.filter(c => c.type === "Tedarikçi"));
    } finally {
      setLoadingPo(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Görüntülenme verisini yükle
    const fetchViews = async () => {
      try {
        const { data: v } = await api.get("/share-views", { params: { refType: "purchase_order" } });
        const newViews = v || {};
        // Yeni görüntülenme bildirimi — aynı id'li toast üst üste yığılmaz
        Object.entries(newViews).forEach(([pid, info]) => {
          const prev = prevViewsRef.current[pid];
          if (info.viewCount > (prev?.viewCount || 0)) {
            // Görüntülenme bildirimi kapatıldı (kullanıcı isteği)
          }
        });
        prevViewsRef.current = newViews;
        setViews(newViews);
      } catch (_) {}
    };
    fetchViews();
    const viewsTimer = setInterval(fetchViews, 30000);
    return () => clearInterval(viewsTimer);
  }, [load]);

  const filtered = pos.filter(p =>
    !q || p.supplier_name?.toLowerCase().includes(q.toLowerCase()) ||
          p.id?.toLowerCase().includes(q.toLowerCase())
  );
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { setPage(1); }, [q]);

  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/purchase-orders/${editing.id}`, form);
      else await api.post("/purchase-orders", form);
      toast.success("Kaydedildi"); setView("list"); setEditing(null); load();
    } catch { toast.error("Kaydedilemedi"); }
  };

  const del = async (po) => {
    if (!window.confirm(`${po.id} silinsin mi?`)) return;
    await api.delete(`/purchase-orders/${po.id}`);
    toast.success("Silindi"); load();
  };

  const handleExportPdf = async (po) => {
    setPdfLoading(po.id);
    try {
      const url = await generatePoPDF(po);
      window.open(url, "_blank");
      toast.success("PDF oluşturuldu");
    } catch (e) {
      toast.error("PDF oluşturulamadı: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPdfLoading(null);
    }
  };

  const handleScanConfirm = (scanResult) => {
    setEditing({
      ...EMPTY_PO,
      supplier_name: scanResult.supplier_name || "",
      order_date: scanResult.invoice_date || new Date().toISOString().slice(0, 10),
      items: (scanResult.items || []).map(it => ({
        code: it.code || "",
        label: it.label || "",
        dimension: it.dimension || "",
        qty: it.qty || 1,
        unit: it.unit || "ad",
        unit_price: it.unit_price || 0,
        currency: it.currency || "TRY",
      })),
      foyer_data: scanResult,
    });
    setView("new");
  };

  return (
    <div className="space-y-4 zen-anim-up" data-testid="purchase-orders-module">
      {/* Başlık */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <ModuleIcon tab="orders" icon={Package} />
          <h2 className="text-2xl font-extrabold tracking-tight">Alış Siparişleri</h2>
        </div>
        {view === "list" && isAdmin && (
          <div className="flex gap-2">
            <button data-testid="po-scan-btn" onClick={() => setView("scan")} className="zen-btn-ghost">
              <Camera className="w-4 h-4" /> Föy Tara
            </button>
            <button data-testid="po-new-btn" onClick={() => { setEditing(null); setView("new"); }} className="zen-btn-primary">
              <Plus className="w-4 h-4" /> Yeni Sipariş
            </button>
          </div>
        )}
        {view !== "list" && (
          <button onClick={() => { setView("list"); setEditing(null); }} className="zen-btn-ghost">
            <X className="w-4 h-4" /> Listeye Dön
          </button>
        )}
      </div>

      {/* Föy Tarama */}
      {view === "scan" && (
        <div className="zen-panel p-6">
          <h3 className="font-bold mb-4 flex items-center gap-2"><Camera className="w-4 h-4 text-[#E5322A]" /> Föy / Fatura Tara</h3>
          <FoyerScanPanel onConfirm={handleScanConfirm} />
        </div>
      )}

      {/* Yeni / Düzenle */}
      {view === "new" && (
        <div className="zen-panel p-6">
          <h3 className="font-bold mb-5 flex items-center gap-2">
            <Package className="w-4 h-4 text-[#E5322A]" />
            {editing?.id ? "Siparişi Düzenle" : "Yeni Alış Siparişi"}
          </h3>
          <PoForm
            initial={editing}
            suppliers={suppliers}
            onSave={save}
            onCancel={() => { setView("list"); setEditing(null); }}
          />
        </div>
      )}

      {/* Liste */}
      {view === "list" && (
        <>
          <div className="flex items-center gap-2 zen-panel px-3 max-w-sm">
            <Search className="w-4 h-4 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Tedarikçi ara..." className="bg-transparent outline-none text-sm py-2 flex-1"
              data-testid="po-search" />
          </div>

          <div className="zen-panel overflow-hidden">
            <table className="zen-table zen-table--compact">
              <thead>
                <tr>
                  <th>Sipariş No</th>
                  <th>Tedarikçi</th>
                  <th>Tarih</th>
                  <th>Kalem</th>
                  <th>Toplam</th>
                  <th>Durum</th>
                  <th className="text-center">Görüldü</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-slate-400 py-8">{loadingPo ? "Yükleniyor…" : "Alış siparişi yok"}</td></tr>
                )}
                {paged.map(po => {
                  const st = STATUS_MAP[po.status] || STATUS_MAP.taslak;
                  return (
                    <tr key={po.id} data-testid={`po-row-${po.id}`}>
                      <td className="font-mono font-bold text-[#E5322A]">{po.id}</td>
                      <td className="font-semibold">{po.supplier_name}</td>
                      <td className="text-slate-500">{po.order_date}</td>
                      <td className="text-center">{(po.items || []).length}</td>
                      <td className="font-semibold">
                        {(po.total_amount || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} {po.total_currency}
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full text-white font-semibold ${st.color}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="text-center whitespace-nowrap" data-testid={`po-seen-${po.id}`}>
                        {views[po.id]?.viewCount ? (
                          <button
                            onClick={() => setSeenPo(po)}
                            data-testid={`po-seen-btn-${po.id}`}
                            className="zen-chip bg-emerald-500/15 text-emerald-600 !text-[10px] hover:bg-emerald-500/25 transition-colors"
                            title={`Görüntüleme Geçmişi (${views[po.id].viewCount} kez) — tümünü gör`}
                          >
                            {lastViewLabel(views[po.id]) || "görüldü ✓"}
                          </button>
                        ) : (
                          <span className="text-slate-400 text-[10px]">—</span>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button
                            data-testid={`po-pdf-${po.id}`}
                            onClick={() => handleExportPdf(po)}
                            disabled={pdfLoading === po.id}
                            className="zen-btn-ghost !p-1.5 text-blue-500 hover:text-blue-700"
                            title="PDF İndir"
                          >
                            {pdfLoading === po.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <FileDown className="w-3.5 h-3.5" />
                            }
                          </button>
                          {isAdmin && (
                            <>
                              <button data-testid={`po-edit-${po.id}`}
                                onClick={() => { setEditing(po); setView("new"); }}
                                className="zen-btn-ghost !p-1.5">
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button data-testid={`po-del-${po.id}`}
                                onClick={() => del(po)}
                                className="zen-btn-ghost !p-1.5 text-red-400 hover:text-red-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <ZenPagination total={filtered.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
          </div>
        </>
      )}
      {seenPo && (
        <QuoteViewsModal
          refType="purchase_order"
          refId={seenPo.id}
          title={`Sipariş ${seenPo.id}`}
          subtitle={seenPo.supplier_name}
          onClose={() => setSeenPo(null)}
        />
      )}
    </div>
  );
}
